import React from 'react';
import styled from 'styled-components';
import StyledLink from '@makeship/core/lib/components/Link';
import { P2 } from '../../../Typography';

import { ProductType } from '../../../../types/common';
import { getPDPTrackingLocation, handleSupportLinkTracking } from '../../../../utils/analytics';
import { FaintCaption } from './sharedStyles';

const SpecsWrapper = styled.div``;

const SingleSpecWrapper = styled.div`
  margin-bottom: 24px;
`;

const SpecsList = styled.ul`
  list-style-type: disc;
`;

const SpecsListItem = styled.li`
  list-style-type: disc;
  margin-left: 30px;
`;

const ProductSpecsText = styled(P2)``;

type ProductDimensions = {
  name: string;
  unit: string;
  default?: string;
  key: string;
};

type ProductCareType = {
  careInstructions?: string;
  material: string[];
  dimensions?: ProductDimensions[] | string[];
  disclaimer?: string;
  weight?: ProductDimensions[];
};

type ProductCare = {
  [type: string]: ProductCareType;
};

const getProductCare = (metafields: Metafields): ProductCare => ({
  [ProductType.Plushie]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material:
      metafields.promotion === 'gitd'
        ? ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)', 'Luminescent Plush Fabric']
        : ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '16',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        key: 'depth',
      },
    ],
    disclaimer: 'Some heights may vary due to hair, or additional accessories.',
  },
  [ProductType.Longboi]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material: ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '11.5',
        key: 'height',
      },
      {
        name: 'Length',
        unit: 'cm',
        default: '50',
        key: 'length',
      },
    ],
    disclaimer: 'Some heights/lengths may vary due to hair, or additional accessories.',
  },
  [ProductType.Sketchbook]: {
    careInstructions: '',
    material: [
      'Sketchbook (60 Pages): 250g Cardboard, 216g Sketch paper',
      'Cover: 350g Cardboard',
      'Artbook (5 pages): 250g Coated Paper, 200g Sketch paper',
      'Label Sleeve: 250g Cardboard',
    ],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '15',
        key: 'height',
      },
      {
        name: 'Length',
        unit: 'cm',
        default: '21',
        key: 'length',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '2',
        key: 'width',
      },
    ],
  },
  [ProductType.Charm]: {
    careInstructions:
      'Remove before exercising or showering, store in pouch, and clean with jewelry polishing cloth to preserve gold plating.',
    material: [
      `18K ${metafields.color === 'gold' ? 'Gold' : 'White Gold'} Plated`,
      'Enamel Graphic',
      'Sterling Silver Base',
    ],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '2',
        key: 'height',
      },
      {
        name: 'Length',
        unit: 'cm',
        default: '2',
        key: 'length',
      },
      {
        name: 'Depth',
        unit: 'mm',
        default: '1',
        key: 'depth',
      },
    ],
  },
  [ProductType.Backpack]: {
    careInstructions: 'Hand wash warm or cool only. Do not machine wash or dry clean.',
    material: [
      'Exterior: Canvas, PU Leather Detailing (Top Handle, Base, Zipper Pull)',
      'Interior: Cotton/Polyester Blend',
      'Adjustable Straps: Canvas and Cotton',
      'Zipper and Buckles: Metal',
      'Graphic: Screen Printing ',
    ],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '43',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '29',
        key: 'length',
      },
    ],
  },
  [ProductType.KeychainPlushie]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material: [
      `Interior (main body): Polypropylene Cotton`,
      'Exterior: Plush Fabric (Ultra Soft Fleece)',
      'Band: Silicone',
    ],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '8',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '5',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '5',
        key: 'depth',
      },
    ],
    disclaimer: 'Some heights may vary due to hair, or additional accessories.',
  },
  [ProductType.JumboPlush]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material:
      metafields.promotion === 'gitd'
        ? ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)', 'Luminescent Plush Fabric']
        : ['Polypropylene Cotton', 'Plush Fabric (Ultra Soft Fleece)'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '24',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '24',
        key: 'width',
      },
    ],
    disclaimer: 'Some heights may vary due to hair, or additional accessories.',
  },
  [ProductType.SquishyPlush]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material: [
      'Interior (main body): Memory Foam',
      'Interior (external structures): PP Cotton Stuffing ',
      'Exterior: Stretchy Plush Fabric and Embroidery',
    ],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '19',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '19.5',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '16',
        key: 'depth',
      },
    ],
    disclaimer: 'Some heights may vary due to hair, or additional accessories.',
  },
  [ProductType.VinylFigure]: {
    careInstructions:
      'To keep your figure in optimal condition limit exposure to these five elements: dust, oil, sunlight, heat, moisture. Long-term exposure to these elements can lead to eventual fading or weakening of Vinyl material.  Figures can be cleaned using either a microfiber or cotton cloth that is dry or lightly damp. If using water be sure to pat (not rub) the figure dry after cleaning. If additional care is needed, please use a Q-tip or cloth (microfiber or cotton) along with household dish soap and warm water to gently spot clean any affected areas.',
    material: ['Coloured PVC', 'Oil Paint and Water-Based Paint', 'Varnish'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '10',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '6',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '5',
        key: 'depth',
      },
    ],
    disclaimer: 'Figure dimensions will vary depending on specific character and pose.',
  },
  [ProductType.FannyPack]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material: ['Exterior: Nylon Polyester', 'Interior: Cotton', 'Zipper: Metal'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '15',
        key: 'height',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '23',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '7.5',
        key: 'depth',
      },
    ],
  },
  [ProductType.EnamelPin2Pack]: {
    material: ['Plated Zinc Alloy'],
    dimensions: [
      {
        name: 'Length',
        unit: 'cm',
        default: '3.8',
        key: 'length',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '3.8',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '0.2',
        key: 'depth',
      },
    ],
    disclaimer: 'Some lengths and/or widths may vary based on specific character pose.',
    weight: [
      {
        name: 'Weight',
        unit: 'grams',
        default: '11.4',
        key: 'weight',
      },
    ],
  },
  [ProductType.EnamelPin3Pack]: {
    material: ['Plated Zinc Alloy'],
    dimensions: [
      {
        name: 'Length',
        unit: 'cm',
        default: '3.8',
        key: 'length',
      },
      {
        name: 'Width',
        unit: 'cm',
        default: '3.8',
        key: 'width',
      },
      {
        name: 'Depth',
        unit: 'cm',
        default: '0.2',
        key: 'depth',
      },
    ],
    disclaimer: 'Some lengths and/or widths may vary based on specific character pose.',
    weight: [
      {
        name: 'Weight',
        unit: 'grams',
        default: '11.4',
        key: 'weight',
      },
    ],
  },
  [ProductType.Doughboi]: {
    careInstructions: 'Handwash warm or cool only. Do not machine wash or dry clean.',
    material: ['Spandex Plush Fabric', '3D Cotton Down Filling'],
    dimensions: [
      {
        name: 'Height',
        unit: 'cm',
        default: '16.0',
        key: 'height',
      },
    ],
    disclaimer: 'Note: Some heights may vary due to additional details.',
  },
  [ProductType.Sticker]: {
    material: ['High quality water-resistant, glossy vinyl'],
    dimensions: ['A5 paper (5.8 x 8.3 inches / 14.8 x 21.1 cm)'],
  },
});

const getProductWarning = (productType: ProductType): string => {
  switch (productType) {
    case ProductType.Plushie:
    case ProductType.Longboi:
    case ProductType.Doughboi:
    case ProductType.VinylFigure:
    case ProductType.KeychainPlushie:
      return 'WARNING: CHOKING HAZARD. Small parts. Not for children under 3 years of age. Adult collectible. Not a toy. Recommended for ages 15 and up.';
    case ProductType.EnamelPin1Pack:
    case ProductType.EnamelPin2Pack:
    case ProductType.EnamelPin3Pack:
      return 'WARNING: CHOKING HAZARD. Small parts and sharp edges. Not for children under 3 years of age. Adult collectible. Not a toy. Recommended for ages 15 and up.';
    default:
      return '';
  }
};

const renderSpecsList = (
  specs: { name: string; key: string; unit: string; default?: string }[] | string[],
  metafields: Metafields,
) => (
  <SpecsList>
    {specs.map((spec) => {
      if (typeof spec === 'string') {
        return (
          <SpecsListItem key={spec}>
            <P2>{spec}</P2>
          </SpecsListItem>
        );
      }

      const value = metafields[spec.key as keyof typeof metafields] || spec.default;

      if (!value) return null; // skip rendering if value is falsy (both metafield and default are missing)

      return (
        <SpecsListItem key={spec.name}>
          <P2>
            {spec.name}: {value}
            {spec.unit}
          </P2>
        </SpecsListItem>
      );
    })}
  </SpecsList>
);

const renderContactMakeshipSupport = (product: Shopify.Product) => (
  <ProductSpecsText>
    Please contact{' '}
    <StyledLink.Primary
      href="https://support.makeship.com/hc/en-us/requests/new"
      onClick={() => handleSupportLinkTracking('open_support_ticket', getPDPTrackingLocation(product), product)}
    >
      Makeship Customer Support
    </StyledLink.Primary>{' '}
    with any questions or if you need assistance.
  </ProductSpecsText>
);

interface CollectibleSpecsProps {
  productType: ProductType;
  metafields: Metafields;
  product: Shopify.Product;
}

const CollectibleSpecs: React.FC<CollectibleSpecsProps> = ({ productType, metafields, product }) => {
  const productCare = getProductCare(metafields)[productType] || {};

  return (
    <SpecsWrapper>
      {productCare.dimensions && (
        <SingleSpecWrapper>
          <FaintCaption>Dimensions</FaintCaption>
          {renderSpecsList(productCare.dimensions, metafields)}
          {productCare.disclaimer && <FaintCaption>{productCare.disclaimer}</FaintCaption>}
        </SingleSpecWrapper>
      )}
      {productCare.weight && (
        <SingleSpecWrapper>
          <FaintCaption>Weight</FaintCaption>
          {renderSpecsList(productCare.weight, metafields)}
        </SingleSpecWrapper>
      )}
      {productCare.material && (
        <SingleSpecWrapper>
          <FaintCaption>Materials</FaintCaption>
          <SpecsList>
            {productCare.material.map((material) => (
              <SpecsListItem key={material}>
                <P2>{material}</P2>
              </SpecsListItem>
            ))}
          </SpecsList>
        </SingleSpecWrapper>
      )}
      {productCare.careInstructions && (
        <SingleSpecWrapper>
          <FaintCaption>Care Instructions</FaintCaption>
          <P2>{productCare.careInstructions}</P2>
        </SingleSpecWrapper>
      )}
      {productType && (
        <SingleSpecWrapper>
          <P2>{getProductWarning(productType)}</P2>
        </SingleSpecWrapper>
      )}
      {renderContactMakeshipSupport(product)}
    </SpecsWrapper>
  );
};

export default CollectibleSpecs;
